import { useEffect, useState } from "react";
import { useMainCompany } from "../../context/MainCompanyContext";
import { useRoleTheme } from "../../context/RoleThemeContext";
import api from "../../api/axios";
import { normalizeTrackingListResponse } from "../../utils/trackingSummariesApi";
import Modal from "../../components/common/AdminModal";
import {
  Search,
  Download,
  Calendar,
  BarChart3,
  FileSpreadsheet,
  RefreshCw,
  TrendingUp,
  Activity,
  Users,
  Building2,
  MapPin,
  DollarSign,
  Mail,
  FileText,
  Target,
  MessageSquare,
  Printer,
  HardDrive,
  UserCheck,
  Clock
} from "lucide-react";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import Toast from "../../components/common/Toast";

const TRACKING_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Windowed page buttons (same pattern as Settings → User management). */
function getPaginationRange(totalPagesArg, currentPage) {
  if (totalPagesArg <= 3) return [...Array(totalPagesArg)].map((_, i) => i + 1);
  if (currentPage === 1) return [1, 2, 3];
  if (currentPage === totalPagesArg) return [totalPagesArg - 2, totalPagesArg - 1, totalPagesArg];
  return [currentPage - 1, currentPage, currentPage + 1];
}

export default function AdminTrackingSummary() {
  const { selectedMain } = useMainCompany();
  const { roleTheme } = useRoleTheme();
  const primaryBtnClasses = `${roleTheme?.colors?.buttons?.primary || 'bg-blue-600 hover:bg-blue-700'} text-white`;

  // State management
  const [trackingSummaries, setTrackingSummaries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [modal, setModal] = useState({ type: null, data: null });
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  // Filters and pagination
  const [filters, setFilters] = useState({
    q: '',
    type: '',
    createdFrom: '',
    createdTo: '',
    hasDownload: '',
    maincompanyid: selectedMain || '',
    groupBy: 'daily' // New groupBy filter
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter options
  const [, setCompanies] = useState([]);
  const [, setStates] = useState([]);
  const [, setCounties] = useState([]);

  // Load initial data
  useEffect(() => {
    loadCompanies();
    loadStates();
    // Intentionally omit loadCompanies, loadStates to run only on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when filters change
  useEffect(() => {
    fetchTrackingSummaries();
    fetchStatistics();
    // Intentionally omit fetchTrackingSummaries, fetchStatistics to avoid refetch on callback identity change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, pageSize, sortBy, sortOrder]);

  /** Stay on a valid page when total or page size changes. */
  useEffect(() => {
    if (loading || total <= 0) return;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [total, pageSize, loading, page]);

  // Update filters when selectedMain changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, maincompanyid: selectedMain || '' }));
  }, [selectedMain]);

  const loadCompanies = async () => {
    try {
      const { data } = await api.get('/admin/main-companies');
      setCompanies(data?.data || []);
    } catch (e) {
      console.error('Failed to load companies:', e);
    }
  };

  const loadStates = async () => {
    try {
      const params = selectedMain ? { maincompanyid: selectedMain } : {};
      const { data } = await api.get('/admin/states', { params });
      setStates(data?.data || []);
    } catch (e) {
      console.error('Failed to load states:', e);
    }
  };

  // Kept for future filter UI (counties by state)
  // eslint-disable-next-line no-unused-vars
  const _loadCounties = async (stateId) => {
    if (!stateId) return;
    try {
      const params = {
        stateId,
        ...(selectedMain ? { maincompanyid: selectedMain } : {})
      };
      const { data } = await api.get('/admin/counties', { params });
      setCounties(data?.data || []);
    } catch (e) {
      console.error('Failed to load counties:', e);
    }
  };

  const fetchTrackingSummaries = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        sort: sortBy,
        order: sortOrder,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== undefined)
        )
      };

      const { data } = await api.get('/admin/tracking-summaries', { params });
      const normalized = normalizeTrackingListResponse(data);
      setTrackingSummaries(normalized.trackingSummaries);
      setTotal(normalized.total);
    } catch (e) {
      console.error('Failed to fetch tracking summaries:', e);
      setTrackingSummaries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const params = {
        dateFrom: filters.createdFrom,
        dateTo: filters.createdTo,
        groupBy: filters.groupBy,
        ...(filters.maincompanyid ? { maincompanyid: filters.maincompanyid } : {}),
        ...(filters.type ? { type: filters.type } : {})
      };

    //  console.log('📊 Fetching statistics with params:', params);
      const { data } = await api.get('/admin/tracking-summaries/statistics', { params });
    //  console.log('📊 Statistics API response:', data);
    //  console.log('📊 Request URL:', `/admin/tracking-summaries/statistics?${new URLSearchParams(params).toString()}`);

      // Handle the complete response structure
      if (data?.data) {
    //    console.log('📊 Processed statistics:', {
     //     groupBy: filters.groupBy,
      //    usageStatsCount: data.data.usageStats?.length || 0,
     //     firstItem: data.data.usageStats?.[0],
     //     totalNetSheetActions: data.data.totalNetSheetActions
        //});

        setStatistics({
          ...data.data.totalNetSheetActions, // Overall totals
          ...data.data.actionDistribution,   // Distribution data
          groupedData: data.data.usageStats, // Period-based data
          period: data.data.period,          // Date range
          filters: data.data.filters         // Applied filters
        });
      } else {
    //    console.log('📊 No data in response');
        setStatistics(null);
      }
    } catch (e) {
      console.error('Failed to fetch statistics:', e);
      setStatistics(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== undefined)
        )
      };

      const resp = await api.get('/admin/tracking-summaries/export', {
        params,
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tracking-summaries-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast('Export completed successfully', 'success');
    } catch (e) {
      console.error('Export failed:', e);
      showToast('Export failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportGroupedStatistics = async () => {
    setExporting(true);
    try {
      // Export the grouped statistics data as JSON/CSV
      if (!statistics.groupedData || statistics.groupedData.length === 0) {
        showToast('No grouped data to export', 'warning');
        return;
      }

      // Create CSV content from grouped data based on groupBy option
      let headers, csvRows;

      switch (filters.groupBy) {
        case 'user':
          headers = [
            'User ID',
            'User Name',
            'Email',
            'Total Records',
            'Total Actions',
            'Emailed',
            'Printed',
            'Downloaded',
            'Texted',
            'Saved'
          ];
          csvRows = statistics.groupedData.map(item => [
            `"${item.userid || item.period || ''}"`,
            `"${item.user?.first_name || ''} ${item.user?.last_name || ''}".trim()` || 'N/A',
            `"${item.user?.email || ''}"`,
            item.totalRecords || 0,
            item.totalNetSheetActions || 0,
            item.totalEmailed || 0,
            (item.totalPrintedDetailed || 0) + (item.totalPrintedQuick || 0),
            item.totalDownloaded || 0,
            item.totalTexted || 0,
            item.totalSaved || 0
          ]);
          break;

        case 'company':
          headers = [
            'Company ID',
            'Company Name',
            'Status',
            'Total Records',
            'Total Actions',
            'Emailed',
            'Printed',
            'Downloaded',
            'Texted',
            'Saved'
          ];
          csvRows = statistics.groupedData.map(item => [
            `"${item.companyid || item.period || ''}"`,
            `"${item.company?.company_name || ''}"`,
            `"${item.company?.is_active ? 'Active' : 'Inactive'}"`,
            item.totalRecords || 0,
            item.totalNetSheetActions || 0,
            item.totalEmailed || 0,
            (item.totalPrintedDetailed || 0) + (item.totalPrintedQuick || 0),
            item.totalDownloaded || 0,
            item.totalTexted || 0,
            item.totalSaved || 0
          ]);
          break;

        case 'type':
          headers = [
            'Transaction Type',
            'Total Records',
            'Total Actions',
            'Emailed',
            'Printed',
            'Downloaded',
            'Texted',
            'Saved'
          ];
          csvRows = statistics.groupedData.map(item => [
            `"${item.period || ''}"`,
            item.totalRecords || 0,
            item.totalNetSheetActions || 0,
            item.totalEmailed || 0,
            (item.totalPrintedDetailed || 0) + (item.totalPrintedQuick || 0),
            item.totalDownloaded || 0,
            item.totalTexted || 0,
            item.totalSaved || 0
          ]);
          break;

        case 'device':
          headers = [
            'Device Type',
            'Total Records',
            'Total Actions',
            'Emailed',
            'Printed',
            'Downloaded',
            'Texted',
            'Saved'
          ];
          csvRows = statistics.groupedData.map(item => [
            `"${item.period || ''}"`,
            item.totalRecords || 0,
            item.totalNetSheetActions || 0,
            item.totalEmailed || 0,
            (item.totalPrintedDetailed || 0) + (item.totalPrintedQuick || 0),
            item.totalDownloaded || 0,
            item.totalTexted || 0,
            item.totalSaved || 0
          ]);
          break;

        case 'daily':
        default:
          headers = [
            'Date',
            'Total Records',
            'Total Actions',
            'Emailed',
            'Printed',
            'Downloaded',
            'Texted',
            'Saved'
          ];
          csvRows = statistics.groupedData.map(item => [
            `"${formatDate(item.period)}"`,
            item.totalRecords || 0,
            item.totalNetSheetActions || 0,
            item.totalEmailed || 0,
            (item.totalPrintedDetailed || 0) + (item.totalPrintedQuick || 0),
            item.totalDownloaded || 0,
            item.totalTexted || 0,
            item.totalSaved || 0
          ]);
          break;
      }

      let csvContent = [
        headers.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');

      // Add summary row if available
      if (statistics.totalNetSheetActions) {
        let summaryHeaders, summaryValues;

        switch (filters.groupBy) {
          case 'user':
            summaryHeaders = 'Total Users:,Total Records:,Total Actions:,Emailed:,Printed:,Downloaded:,Texted:,Saved:';
            summaryValues = `${statistics.groupedData?.length || 0},${statistics.totalRecords || 0},${statistics.totalNetSheetActions},${statistics.totalEmailed || 0},${(statistics.totalPrintedDetailed || 0) + (statistics.totalPrintedQuick || 0)},${statistics.totalDownloaded || 0},${statistics.totalTexted || 0},${statistics.totalSaved || 0}`;
            break;

          case 'company':
            summaryHeaders = 'Total Companies:,Total Records:,Total Actions:,Emailed:,Printed:,Downloaded:,Texted:,Saved:';
            summaryValues = `${statistics.groupedData?.length || 0},${statistics.totalRecords || 0},${statistics.totalNetSheetActions},${statistics.totalEmailed || 0},${(statistics.totalPrintedDetailed || 0) + (statistics.totalPrintedQuick || 0)},${statistics.totalDownloaded || 0},${statistics.totalTexted || 0},${statistics.totalSaved || 0}`;
            break;

          case 'type':
          case 'device':
            summaryHeaders = 'Total Types:,Total Records:,Total Actions:,Emailed:,Printed:,Downloaded:,Texted:,Saved:';
            summaryValues = `${statistics.groupedData?.length || 0},${statistics.totalRecords || 0},${statistics.totalNetSheetActions},${statistics.totalEmailed || 0},${(statistics.totalPrintedDetailed || 0) + (statistics.totalPrintedQuick || 0)},${statistics.totalDownloaded || 0},${statistics.totalTexted || 0},${statistics.totalSaved || 0}`;
            break;

          case 'daily':
          default:
            summaryHeaders = 'Total Days:,Total Records:,Total Actions:,Emailed:,Printed:,Downloaded:,Texted:,Saved:';
            summaryValues = `${statistics.groupedData?.length || 0},${statistics.totalRecords || 0},${statistics.totalNetSheetActions},${statistics.totalEmailed || 0},${(statistics.totalPrintedDetailed || 0) + (statistics.totalPrintedQuick || 0)},${statistics.totalDownloaded || 0},${statistics.totalTexted || 0},${statistics.totalSaved || 0}`;
            break;
        }

        csvContent += `\n\n${summaryHeaders}`;
        csvContent += `\n${summaryValues}`;
      }

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const groupByLabel = filters.groupBy === 'daily' ? 'daily' :
                           filters.groupBy === 'user' ? 'by-user' :
                           filters.groupBy === 'company' ? 'by-company' :
                           filters.groupBy === 'type' ? 'by-type' :
                           filters.groupBy === 'device' ? 'by-device' : 'grouped';
      link.setAttribute('download', `tracking-summary-${groupByLabel}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast('Grouped statistics exported successfully', 'success');
    } catch (e) {
      console.error('Grouped statistics export failed:', e);
      showToast('Export failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleSort = (field) => {
    setPage(1);
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: 'success' }), 3000);
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getActionCount = (actions) => {
    if (!actions) return 0;
    return Object.values(actions).reduce((sum, count) => sum + (count || 0), 0);
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * pageSize, total);

  // Loading skeleton
  if (loading && !trackingSummaries.length) {
    return (
      <div className="p-4 space-y-6">
        <AdminPageHeader
          title="Tracking Summary Reports"
          actions={<div className="h-10 w-32 bg-gray-200 animate-pulse rounded-lg"></div>}
          filters={<div className="h-12 w-full bg-gray-200 animate-pulse rounded-lg"></div>}
        />

        {/* Statistics Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="h-4 w-24 bg-gray-200 animate-pulse rounded mb-2"></div>
              <div className="h-8 w-16 bg-gray-200 animate-pulse rounded"></div>
              <div className="h-3 w-20 bg-gray-200 animate-pulse rounded mt-1"></div>
            </div>
          ))}
        </div>

        {/* Table Skeleton */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  {[...Array(8)].map((_, i) => (
                    <th key={i} className="px-4 py-4">
                      <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(10)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                    {[...Array(8)].map((_, j) => (
                      <td key={j} className="px-4 py-4">
                        <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <AdminPageHeader
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: roleTheme?.colors?.sidebar?.active || 'linear-gradient(to right, rgb(59 130 246), rgb(147 51 234))' }}>
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tracking Summary Reports</h1>
              <p className="text-sm text-gray-500">Comprehensive analytics for net sheet usage and user activity</p>
            </div>
          </div>
        }
        actions={(
          <div className="flex gap-3">
            <button
              onClick={fetchTrackingSummaries}
              className={`flex items-center gap-2 px-4 py-2 ${primaryBtnClasses} rounded-lg transition-all shadow-md hover:shadow-lg`}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Data
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !trackingSummaries.length}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        )}
        filters={(
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, MRSID..."
                  value={filters.q}
                  onChange={(e) => handleFilterChange('q', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]"
                />
              </div>
            </div>

            {/* Transaction Type */}
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]"
              >
                <option value="">All Types</option>
                <option value="buyer">Buyer</option>
                <option value="seller">Seller</option>
                <option value="refinance">Refinance</option>
              </select>
            </div>

            {/* Date From */}
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.createdFrom}
                onChange={(e) => handleFilterChange('createdFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]"
              />
            </div>

            {/* Date To */}
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.createdTo}
                onChange={(e) => handleFilterChange('createdTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]"
              />
            </div>

            {/* Download Filter */}
            {/*   <div className="min-w-[120px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Download
              </label>
              <select
                value={filters.hasDownload}
                onChange={(e) => handleFilterChange('hasDownload', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]"
              >
                <option value="">All</option>
                <option value="true">Downloaded</option>
                <option value="false">Not Downloaded</option>
              </select>
            </div> */}

            {/* Group By Filter */}
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group By
              </label>
              <select
                value={filters.groupBy}
                onChange={(e) => handleFilterChange('groupBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)]"
              >
                <option value="daily">Daily</option>
                <option value="type">Transaction Type</option>
                <option value="company">Company</option>
                <option value="device">Device Type</option>
                <option value="user">User</option>
              </select>
            </div>

            {/* Clear Filters */}
            <button
              onClick={() => {
                setFilters({
                  q: '',
                  type: '',
                  createdFrom: '',
                  createdTo: '',
                  hasDownload: '',
                  maincompanyid: selectedMain || '',
                  groupBy: 'daily'
                });
                setPage(1);
              }}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        )}
      />

      {/* Quick Overview */}
      <div className="rounded-xl p-6 mb-6 border border-[var(--role-border-light)]" style={{ background: roleTheme?.gradientBg?.primary || 'linear-gradient(to right, rgb(238 242 255), rgb(233 213 255))' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: roleTheme?.colors?.sidebar?.active || 'linear-gradient(to right, rgb(99 102 241), rgb(147 51 234))' }}>
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Quick Overview</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-white/50">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-medium text-gray-700">Total Reports</span>
            </div>
            <p className="text-2xl font-bold text-indigo-700 mt-2">{total}</p>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-white/50">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-700">Active Users</span>
            </div>
            <p className="text-2xl font-bold text-purple-700 mt-2">
              {new Set(trackingSummaries.map(item => item.user?.id).filter(Boolean)).size}
            </p>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-white/50">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-gray-700">Companies</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700 mt-2">
              {new Set(trackingSummaries.map(item => item.maincompanyid).filter(Boolean)).size}
            </p>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-lg p-4 border border-white/50">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              <span className="text-sm font-medium text-gray-700">Date Range</span>
            </div>
            <p className="text-sm font-bold text-orange-700 mt-2">
              {filters.createdFrom && filters.createdTo ?
                `${formatDate(filters.createdFrom)} - ${formatDate(filters.createdTo)}` :
                'All time'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="space-y-6">
          {/* Net Sheet Usage Statistics 
          {statistics.groupedData && statistics.groupedData.length > 0 && (
            <div className={`border rounded-lg p-4 mb-4 ${
              (filters.groupBy === 'user' && statistics.groupedData[0]?.period && typeof statistics.groupedData[0].period === 'string' && !statistics.groupedData[0].period.startsWith('user_')) ||
              (filters.groupBy === 'type' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
              (filters.groupBy === 'company' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
              (filters.groupBy === 'device' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
              (filters.groupBy === 'daily' && statistics.groupedData[0]?.period && !/^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period))
                ? 'bg-red-50 border-red-200'
                : 'bg-[var(--role-border-light)]/30 border-[var(--role-border-light)]'
            }`}>
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-2 ${
                  (filters.groupBy === 'user' && statistics.groupedData[0]?.period && typeof statistics.groupedData[0].period === 'string' && !statistics.groupedData[0].period.startsWith('user_')) ||
                  (filters.groupBy === 'type' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                  (filters.groupBy === 'company' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                  (filters.groupBy === 'device' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                  (filters.groupBy === 'daily' && statistics.groupedData[0]?.period && !/^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period))
                    ? 'text-red-800'
                    : 'text-blue-800'
                }`}>
                  <BarChart3 className="w-5 h-5" />
                  <span className="font-medium">
                    Grouped by {filters.groupBy === 'daily' ? 'Day' : filters.groupBy === 'type' ? 'Transaction Type' : filters.groupBy === 'company' ? 'Company' : filters.groupBy === 'device' ? 'Device Type' : 'User'}
                    {(filters.groupBy === 'user' && statistics.groupedData[0]?.period && typeof statistics.groupedData[0].period === 'string' && !statistics.groupedData[0].period.startsWith('user_')) ||
                     (filters.groupBy === 'type' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                     (filters.groupBy === 'company' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                     (filters.groupBy === 'device' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                     (filters.groupBy === 'daily' && statistics.groupedData[0]?.period && !/^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) && (
                      <span className="ml-2 text-red-600 font-bold">⚠️ BACKEND ISSUE</span>
                    )}
                  </span>
                </div>
                {statistics.period && (
                  <div className={`text-sm ${
                    (filters.groupBy === 'user' && statistics.groupedData[0]?.period && typeof statistics.groupedData[0].period === 'string' && !statistics.groupedData[0].period.startsWith('user_')) ||
                    (filters.groupBy === 'type' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                    (filters.groupBy === 'company' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                    (filters.groupBy === 'device' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                    (filters.groupBy === 'daily' && statistics.groupedData[0]?.period && !/^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period))
                      ? 'text-red-600'
                      : 'text-blue-600'
                  }`}>
                    Period: {formatDate(statistics.period.from)} - {formatDate(statistics.period.to)}
                  </div>
                )}
              </div>
              <p className={`text-sm mt-1 ${
                (filters.groupBy === 'user' && statistics.groupedData[0]?.period && typeof statistics.groupedData[0].period === 'string' && !statistics.groupedData[0].period.startsWith('user_')) ||
                (filters.groupBy === 'type' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                (filters.groupBy === 'company' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                (filters.groupBy === 'device' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                (filters.groupBy === 'daily' && statistics.groupedData[0]?.period && !/^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period))
                  ? 'text-red-600'
                  : 'text-blue-600'
              }`}>
                Showing {statistics.groupedData.length} {filters.groupBy === 'daily' ? 'days' : 'groups'} of data
                {statistics.totalRecords && ` • Total Records: ${statistics.totalRecords}`}
                {(filters.groupBy === 'user' && statistics.groupedData[0]?.period && typeof statistics.groupedData[0].period === 'string' && !statistics.groupedData[0].period.startsWith('user_')) ||
                 (filters.groupBy === 'type' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                 (filters.groupBy === 'company' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                 (filters.groupBy === 'device' && statistics.groupedData[0]?.period && /^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) ||
                 (filters.groupBy === 'daily' && statistics.groupedData[0]?.period && !/^\d{4}-\d{2}-\d{2}$/.test(statistics.groupedData[0].period)) && (
                  <span className="block mt-1 font-medium">
                    ⚠️ Backend is returning date-based data instead of {filters.groupBy} grouping
                  </span>
                )}
              </p>
            </div>
          )}
*/}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full"></div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Actions</p>
                  <p className="text-3xl font-bold mt-1">{statistics.totalNetSheetActions || 0}</p>
                  <p className="text-blue-200 text-xs mt-2">Net Sheet Usage</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Activity className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{width: '100%'}}></div>
              </div>
            </div>

            <div className="relative overflow-hidden bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg text-white">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full"></div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Emailed</p>
                  <p className="text-3xl font-bold mt-1">{statistics.totalEmailed || 0}</p>
                  <p className="text-green-200 text-xs mt-2">{statistics.emailed || 0}% of total</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Mail className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{width: `${statistics.emailed || 0}%`}}></div>
              </div>
            </div>

            <div className="relative overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-lg text-white">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full"></div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Printed</p>
                  <p className="text-3xl font-bold mt-1">{(statistics.totalPrintedDetailed || 0) + (statistics.totalPrintedQuick || 0)}</p>
                  <p className="text-purple-200 text-xs mt-2">{statistics.printed || 0}% of total</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Printer className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{width: `${statistics.printed || 0}%`}}></div>
              </div>
            </div>

            <div className="relative overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-lg text-white">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full"></div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium">Downloaded</p>
                  <p className="text-3xl font-bold mt-1">{statistics.totalDownloaded || 0}</p>
                  <p className="text-orange-200 text-xs mt-2">{statistics.downloaded || 0}% of total</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Download className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{width: `${statistics.downloaded || 0}%`}}></div>
              </div>
            </div>

            <div className="relative overflow-hidden bg-gradient-to-br from-cyan-500 to-cyan-600 p-6 rounded-xl shadow-lg text-white">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full"></div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-sm font-medium">Texted</p>
                  <p className="text-3xl font-bold mt-1">{statistics.totalTexted || 0}</p>
                  <p className="text-cyan-200 text-xs mt-2">{statistics.texted || 0}% of total</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <MessageSquare className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{width: `${statistics.texted || 0}%`}}></div>
              </div>
            </div>

            <div className="relative overflow-hidden bg-gradient-to-br from-cyan-500 to-cyan-600 p-6 rounded-xl shadow-lg text-white">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-bl-full"></div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-100 text-sm font-medium">Saved</p>
                  <p className="text-3xl font-bold mt-1">{statistics.totalSaved || 0}</p>
                  <p className="text-cyan-200 text-xs mt-2">{statistics.saved || 0}% of total</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                <HardDrive className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 bg-white/20 rounded-full h-2">
                <div className="bg-white h-2 rounded-full" style={{width: `${statistics.texted || 0}%`}}></div>
              </div>
            </div>

          </div>

          {/* Action Breakdown 
          {statistics.breakdown && (
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-2 rounded-lg">
                  <PieChart className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Action Breakdown</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200 text-center hover:shadow-md transition-shadow">
                  <div className="bg-green-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-green-700">{statistics.breakdown.detailedEmail || 0}</div>
                  <div className="text-xs text-green-600 font-medium">Detailed Email</div>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-lg border border-emerald-200 text-center hover:shadow-md transition-shadow">
                  <div className="bg-emerald-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-emerald-700">{statistics.breakdown.quickEmail || 0}</div>
                  <div className="text-xs text-emerald-600 font-medium">Quick Email</div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200 text-center hover:shadow-md transition-shadow">
                  <div className="bg-purple-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Printer className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-purple-700">{statistics.breakdown.detailedPrint || 0}</div>
                  <div className="text-xs text-purple-600 font-medium">Detailed Print</div>
                </div>

                <div className="bg-gradient-to-br from-violet-50 to-violet-100 p-4 rounded-lg border border-violet-200 text-center hover:shadow-md transition-shadow">
                  <div className="bg-violet-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-violet-700">{statistics.breakdown.quickPrint || 0}</div>
                  <div className="text-xs text-violet-600 font-medium">Quick Print</div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200 text-center hover:shadow-md transition-shadow">
                  <div className="bg-orange-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-orange-700">{statistics.breakdown.download || 0}</div>
                  <div className="text-xs text-orange-600 font-medium">Download</div>
                </div>

                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-lg border border-cyan-200 text-center hover:shadow-md transition-shadow">
                  <div className="bg-cyan-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-cyan-700">{statistics.breakdown.text || 0}</div>
                  <div className="text-xs text-cyan-600 font-medium">Text Message</div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200 text-center hover:shadow-md transition-shadow">
                  <div className="bg-gray-500 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <HardDrive className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-gray-700">{statistics.breakdown.save || 0}</div>
                  <div className="text-xs text-gray-600 font-medium flex items-center justify-center gap-1">
                    <Save className="w-3 h-3" />
                    Save
                  </div>
                </div>
              </div>
            </div>
          )}
*/}
          {/* Group By Statistics (if applicable) */}
          {statistics.groupedData && statistics.groupedData.length > 0 && (
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-2 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Statistics by {filters.groupBy === 'daily' ? 'Day' :
                                   filters.groupBy === 'type' ? 'Transaction Type' :
                                   filters.groupBy === 'company' ? 'Company' :
                                   filters.groupBy === 'device' ? 'Device Type' : 'User'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {statistics.groupedData.length} {filters.groupBy === 'daily' ? 'days' : 'items'} of data
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleExportGroupedStatistics}
                  disabled={exporting}
                  className={`flex items-center gap-2 px-4 py-2 ${primaryBtnClasses} rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  {exporting ? 'Exporting...' : 'Export Grouped Data'}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">
                        {filters.groupBy === 'daily' ? 'Date' :
                          filters.groupBy === 'type' ? 'Type' :
                            filters.groupBy === 'company' ? 'Company' :
                              filters.groupBy === 'device' ? 'Device' : 'User'}
                      </th>
                      {/*  <th className="px-4 py-3 text-right font-semibold text-gray-900">Records</th>   */}
                      <th className="px-4 py-3 text-right font-semibold text-gray-900">Total Actions</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900">Emailed</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900">Printed</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900">Downloaded</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900">Texted</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-900">Saved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.groupedData.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {filters.groupBy === 'daily' ? formatDate(item.period) :
                           filters.groupBy === 'company' ? (
                             item.company ? (
                               <div>
                                 <div className="font-semibold">{item.company.company_name}</div>
                                 <div className="text-sm text-gray-500">ID: {item.company.id}</div>
                                 <div className="text-xs text-gray-400">
                                   Status: {item.company.is_active ? 'Active' : 'Inactive'}
                                 </div>
                               </div>
                             ) : item.companyName || item.groupKey || item.period
                           ) :
                             filters.groupBy === 'user' ? (
                               item.user ? (
                                 <div>
                                   <div className="font-semibold">{item.user.first_name} {item.user.last_name}</div>
                                   <div className="text-sm text-gray-500">{item.user.email}</div>
                                   <div className="text-xs text-gray-400">@{item.user.username}</div>
                                 </div>
                               ) : typeof item.period === 'string' && item.period.startsWith('user_') ?
                                 `User ${item.period.replace('user_', '')}` :
                                 item.groupKey || item.period
                             ) :
                             item.groupKey || item.period}
                        </td>
                        {/*<td className="px-4 py-3 text-right text-gray-900">{item.totalRecords || item.totalNetSheetActions || 0}</td>*/}
                        <td className="px-4 py-3 text-right text-gray-900">{item.totalNetSheetActions || 0}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{item.totalEmailed || 0}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{(item.totalPrintedDetailed || 0) + (item.totalPrintedQuick || 0)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{item.totalDownloaded || 0}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{item.totalTexted || 0}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{item.totalSaved || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <tr>
                <th
                  className="px-6 py-5 text-left font-bold text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" />
                    Date
                    {sortBy === 'created_at' && (
                      <span className="text-xs font-bold text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-5 text-left font-bold text-slate-700">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-slate-500" />
                    Prepared By
                  </div>
                </th>
                <th className="px-6 py-5 text-left font-bold text-slate-700">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-slate-500" />
                    Prepared For
                  </div>
                </th>
                <th className="px-6 py-5 text-left font-bold text-slate-700">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-500" />
                    Property Location
                  </div>
                </th>
                <th
                  className="px-6 py-5 text-left font-bold text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => handleSort('property_value')}
                >
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-500" />
                    Property Value
                    {sortBy === 'property_value' && (
                      <span className="text-xs font-bold text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-6 py-5 text-left font-bold text-slate-700">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-slate-500" />
                    Type
                  </div>
                </th>
                <th className="px-6 py-5 text-left font-bold text-slate-700">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-slate-500" />
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {trackingSummaries.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-gray-500">
                    {loading ? 'Loading...' : 'No tracking summaries found'}
                  </td>
                </tr>
              ) : (
                trackingSummaries.map((item, index) => (
                  <tr key={item.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                    index % 2 === 0 ? 'bg-white' : 'bg-slate-25'
                  }`}>
                    <td className="px-6 py-4 text-slate-900 font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Clock className="w-4 h-4 text-blue-600" />
                        </div>
                        {formatDate(item.created_at)}
                      </div>
                    </td>
                    {/*    <td className="px-4 py-4 text-gray-900 font-mono">
                      {item.mrsid || '-'}
                    </td> */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <UserCheck className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">
                            {item.user?.first_name} {item.user?.last_name}
                          </div>
                          <div className="text-sm text-slate-500">
                            {item.user?.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Target className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="font-medium text-slate-900">{item.preparedfor || '-'}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <MapPin className="w-4 h-4 text-orange-600" />
                        </div>
                        <span className="text-slate-700">{item.address || '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="font-bold text-slate-900 text-lg">{formatCurrency(item.salesprice)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-bold rounded-full ${
                        item.type === 'seller' ? 'bg-emerald-100 text-emerald-800' :
                        item.type === 'seller2Net' ? 'bg-blue-100 text-blue-800' :
                        item.type === 'buyer' ? 'bg-purple-100 text-purple-800' :
                        item.type === 'refinance' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                        <TrendingUp className="w-4 h-4" />
                        {item.type === 'seller2Net' ? 'Seller 2Net' : item.type || 'Unknown'}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                          <Activity className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <span className="font-bold text-slate-900 text-lg">{getActionCount(item.actions)}</span>
                          <div className="text-xs text-slate-500">actions taken</div>
                        </div>
                      </div>
                    </td>
                    {/*    <td className="px-4 py-4">
                      {item.has_download ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                          <Download className="w-3 h-3" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">
                          No
                        </span>
                      )}
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — matches list API pagination.total / limit */}
        {!loading && total > 0 && (
          <div className="flex flex-col lg:flex-row flex-wrap items-stretch lg:items-center justify-between gap-4 px-4 py-4 bg-gray-50 border-t border-gray-200">
            <span className="text-sm text-gray-700 tabular-nums">
              Showing {showingFrom}–{showingTo} of {total}
            </span>

            <div className="flex items-center gap-2 text-sm text-gray-700">
              <label htmlFor="admin-tracking-page-size" className="whitespace-nowrap font-medium">
                Rows per page
              </label>
              <select
                id="admin-tracking-page-size"
                value={pageSize}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (TRACKING_PAGE_SIZE_OPTIONS.includes(n)) {
                    setPageSize(n);
                    setPage(1);
                  }
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {TRACKING_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {totalPages > 1 && (
              <nav className="flex items-center gap-1 flex-wrap" aria-label="Table pagination">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="min-w-[40px] px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-45 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                {getPaginationRange(totalPages, page).map((pNum) => (
                  <button
                    type="button"
                    key={pNum}
                    onClick={() => setPage(pNum)}
                    className={`min-w-[40px] px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                      page === pNum
                        ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                        : "border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
                    }`}
                  >
                    {pNum}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="min-w-[40px] px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-45 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            )}
          </div>
        )}
      </div>

      {/* Info Modal */}
      {modal.type === 'info' && (
        <Modal title={modal.data?.title || 'Info'} onClose={() => setModal({ type: null, data: null })}>
          <div>
            <p className="text-gray-700">{modal.data?.message}</p>
            <div className="mt-4 flex justify-end">
              <button
                className={`px-3 py-1 ${primaryBtnClasses} rounded`}
                onClick={() => setModal({ type: null, data: null })}
              >
                OK
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Global toast */}
      {toast.visible && (
        <Toast message={toast.message} type={toast.type} visible={toast.visible} />
      )}
    </div>
  );
}